import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ClipboardList, HelpCircle, Mail, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import userMetadataApi from '../../api/userMetadataApi';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useUser } from '../../context/UserContext';
import TutorialOverlay from './TutorialOverlay';
import { TutorialPageKey, tutorialConfigs, tutorialPageForPath } from './tutorialConfig';
import {
    SETUP_GUIDE_TOUR_RUNNING_KEY,
    clearTutorialRuntime,
    setTutorialRuntimePage,
} from './tutorialRuntime';

type GuidePage = {
    key: TutorialPageKey;
    label: string;
    path: string;
};

type SetupStep = {
    label: string;
    description?: string;
    complete: boolean;
    actionLabel?: string;
    onAction?: () => void;
    renderActions?: () => React.ReactNode;
};

const guidePages: GuidePage[] = [
    { key: 'home', label: 'Home', path: '/home' },
    { key: 'dashboard', label: 'Cash', path: '/cash' },
    { key: 'margin', label: 'Margin', path: '/margin' },
    { key: 'documents', label: 'Drive', path: '/drive' },
    { key: 'ledger', label: 'Ledger', path: '/ledger' },
];

const SETUP_GUIDE_ENDED_KEY_PREFIX = `finz:setup-guide-ended:${guidePages
    .map(({ key }) => `${key}-${tutorialConfigs[key].version}`)
    .join('|')}`;
const completionKey = (pageKey: TutorialPageKey) => `${pageKey}:${tutorialConfigs[pageKey].version}`;

export default function PageTutorial() {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { activeBusinessId } = useWorkspace();
    const { userMetadata, isLoading, isAdmin } = useUser();
    const pageKey = tutorialPageForPath(location.pathname);
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const [isTourRunning, setIsTourRunning] = useState(() => sessionStorage.getItem(SETUP_GUIDE_TOUR_RUNNING_KEY) === 'true');
    const setupGuideEndedKey = userMetadata?.user_id
        ? `${SETUP_GUIDE_ENDED_KEY_PREFIX}:${userMetadata.user_id}:${activeBusinessId || userMetadata.business_id || 'default'}`
        : null;
    const [hasEndedGuide, setHasEndedGuide] = useState(false);

    const completedTutorialKeys = useMemo(() => {
        const completedPages = userMetadata?.tutorial_state?.completed_pages || {};
        return new Set(
            guidePages
                .filter(({ key }) => (completedPages[key]?.version || 0) >= tutorialConfigs[key].version)
                .map(({ key }) => completionKey(key)),
        );
    }, [userMetadata?.tutorial_state?.completed_pages]);

    const completeAllMutation = useMutation({
        mutationFn: async () => {
            await Promise.all(
                guidePages.map(({ key }) =>
                    userMetadataApi.completeTutorialPage(key, tutorialConfigs[key].version, activeBusinessId || undefined),
                ),
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userMetadata'] });
            queryClient.invalidateQueries({ queryKey: ['userMetadata', activeBusinessId] });
        },
    });

    const linkedQbo = Boolean(userMetadata?.connections?.linked_qbo);
    const linkedBank = Boolean(userMetadata?.connections?.linked_stripe);
    const onboardingComplete = Boolean(userMetadata?.feature_flags?.is_onboarding_completed);
    const completedTutorialCount = completedTutorialKeys.size;
    const allTutorialsCompleted = completedTutorialCount >= guidePages.length;
    const canShowSetupGuideOnRoute = !location.pathname.startsWith('/settings') && !location.pathname.startsWith('/history');
    const shouldShowSetupGuide = canShowSetupGuideOnRoute && !hasEndedGuide && (!onboardingComplete || !allTutorialsCompleted);
    const isSetupGuideVisible = shouldShowSetupGuide && isPanelOpen;
    const currentConfig = pageKey ? tutorialConfigs[pageKey] : null;

    useEffect(() => {
        document.body.classList.toggle('finz-setup-guide-open', isSetupGuideVisible);
        window.dispatchEvent(new CustomEvent('finz:setup-guide:visibility', {
            detail: { open: isSetupGuideVisible },
        }));
        return () => {
            document.body.classList.remove('finz-setup-guide-open');
            window.dispatchEvent(new CustomEvent('finz:setup-guide:visibility', {
                detail: { open: false },
            }));
        };
    }, [isSetupGuideVisible]);

    useEffect(() => {
        setHasEndedGuide(Boolean(setupGuideEndedKey && localStorage.getItem(setupGuideEndedKey) === 'true'));
    }, [setupGuideEndedKey]);

    useEffect(() => {
        if (sessionStorage.getItem(SETUP_GUIDE_TOUR_RUNNING_KEY) !== 'true') return;
        setIsPanelOpen(false);
        if (pageKey) {
            setTutorialRuntimePage(pageKey);
            setIsTourRunning(true);
        } else {
            setIsTourRunning(false);
        }
    }, [pageKey]);

    if (isLoading || !userMetadata || isAdmin) return null;

    const closeGuidePermanently = async () => {
        if (setupGuideEndedKey) {
            localStorage.setItem(setupGuideEndedKey, 'true');
        }
        clearTutorialRuntime();
        setHasEndedGuide(true);
        setIsPanelOpen(false);
        setIsTourRunning(false);
        await completeAllMutation.mutateAsync();
    };

    const startTour = () => {
        setIsPanelOpen(false);
        setIsTourRunning(true);
        const firstIncompletePage = guidePages.find(({ key }) => !completedTutorialKeys.has(completionKey(key)));
        const firstPage = firstIncompletePage || guidePages[0];
        setTutorialRuntimePage(firstPage.key);
        if (pageKey !== firstPage.key) {
            navigate(firstPage.path);
        }
    };

    const handleTourPageComplete = () => {
        if (!pageKey) return;
        const currentIndex = guidePages.findIndex(({ key }) => key === pageKey);
        const nextPage = guidePages.slice(currentIndex + 1)[0];

        if (nextPage) {
            setTutorialRuntimePage(nextPage.key);
            navigate(nextPage.path);
            return;
        }

        setIsTourRunning(false);
        clearTutorialRuntime();
        if (onboardingComplete) {
            if (setupGuideEndedKey) {
                localStorage.setItem(setupGuideEndedKey, 'true');
            }
            setHasEndedGuide(true);
            setIsPanelOpen(false);
        } else {
            setIsPanelOpen(true);
        }
    };

    if (!shouldShowSetupGuide && !isTourRunning) return null;

    const setupSteps: SetupStep[] = [
        {
            label: 'Connect QuickBooks',
            description: 'Import your revenue and expense data to start tracking performance.',
            complete: linkedQbo,
            actionLabel: 'Connect QuickBooks',
            onAction: () => navigate('/settings?tab=Connectors'),
        },
        {
            label: 'Link your bank accounts',
            description: 'Connect your bank data so Finz can track cash movement.',
            complete: linkedBank,
            actionLabel: 'Connect accounts',
            onAction: () => navigate('/settings?tab=Connectors'),
        },
        {
            label: 'Start using Finz',
            description: 'Take a quick walkthrough of your workspace and see how everything comes together.',
            complete: onboardingComplete && allTutorialsCompleted,
            renderActions: () => (
                <div className="mt-4 flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={startTour}
                        className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-[#1F2937] px-4 text-sm font-medium leading-5 text-white hover:bg-[#111827]"
                    >
                        Start tour
                    </button>
                    <button
                        type="button"
                        onClick={() => void closeGuidePermanently()}
                        disabled={completeAllMutation.isPending}
                        className="inline-flex h-9 items-center justify-center rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium leading-5 text-[#1F2937] hover:bg-gray-50 disabled:opacity-50"
                    >
                        End tour
                    </button>
                </div>
            ),
        },
    ];
    const completedSetupCount = setupSteps.filter((step) => step.complete).length;
    const activeIndex = Math.min(
        Math.max(setupSteps.findIndex((step) => !step.complete), 0),
        setupSteps.length - 1,
    );

    return (
        <>
            {isTourRunning && currentConfig && (
                <TutorialOverlay
                    key={`${currentConfig.pageKey}:${currentConfig.version}:guided-tour`}
                    config={currentConfig}
                    forceOpen
                    onClose={() => {
                        clearTutorialRuntime();
                        setIsTourRunning(false);
                    }}
                    onComplete={handleTourPageComplete}
                />
            )}

            {isSetupGuideVisible && (
                <aside className="fixed bottom-0 right-0 top-[50px] z-[1200] flex w-full min-w-0 flex-col overflow-hidden border border-y-0 border-r-0 border-[#E9E9E9] bg-white font-sans text-[#1F2937] shadow-sm sm:top-0 sm:w-[30%] sm:min-w-[350px] sm:max-w-[300px]">
                    <header className="flex h-[48px] shrink-0 items-center justify-between border-b border-gray-100 bg-[#F1F2F4] px-4">
                        <div className="flex items-center gap-1">
                            <ClipboardList className="h-[17px] w-[17px] text-[#1F2937]" strokeWidth={1.75} />
                            <h2 className="text-sm font-bold text-slate-800">Set Up Guide</h2>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsPanelOpen(false)}
                            aria-label="Close setup guide"
                            className="text-gray-500 transition-colors hover:text-slate-800"
                            title="Close"
                        >
                            <X size={18} />
                        </button>
                    </header>

                    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
                        <section className="flex flex-col gap-3">
                            <div className="text-sm font-semibold leading-5 text-[#1F2937]">
                                {completedSetupCount} of {setupSteps.length} Completed
                            </div>
                            <div className="h-2.5 overflow-hidden rounded-full border border-[#E9E9E9] bg-white">
                                <div
                                    className="h-full rounded-full bg-[#5B5DE4] transition-all"
                                    style={{ width: `${Math.max(2, (completedSetupCount / setupSteps.length) * 100)}%` }}
                                />
                            </div>
                        </section>

                        <section className="flex flex-1 flex-col gap-3">
                            {setupSteps.map((step, index) => {
                                const isActive = index === activeIndex;
                                return (
                                    <div
                                        key={step.label}
                                        className={isActive ? 'flex items-start gap-2 rounded-lg border border-[#E9E9E9] p-3' : 'flex min-h-10 items-start gap-2 px-3 py-2'}
                                    >
                                        <div className={`w-6 shrink-0 text-sm font-medium leading-5 ${step.complete ? 'text-[#5B5DE4]' : isActive ? 'text-[#1F2937]' : 'text-[#697079]'}`}>
                                            {step.complete ? <Check size={15} /> : `${index + 1}.`}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className={`text-sm font-semibold leading-5 ${isActive ? 'text-[#1F2937]' : 'text-[#697079]'}`}>
                                                {step.label}
                                            </div>
                                            {isActive && step.description && (
                                                <p className="mt-1 text-xs leading-4 text-[#4C545F]">
                                                    {step.description}
                                                </p>
                                            )}
                                            {isActive && step.actionLabel && (
                                                <button
                                                    type="button"
                                                    onClick={step.onAction}
                                                    className="mt-4 inline-flex h-9 items-center justify-center rounded-lg bg-[#1F2937] px-4 text-sm font-medium leading-5 text-white hover:bg-[#111827]"
                                                >
                                                    {step.actionLabel}
                                                </button>
                                            )}
                                            {isActive && step.renderActions?.()}
                                        </div>
                                    </div>
                                );
                            })}
                        </section>

                        <div className="border-t border-[#F1F2F3] pt-4">
                            <a href="mailto:support@finzcard.com" className="flex items-start gap-2 py-2">
                                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#1F2937]" strokeWidth={1.75} />
                                <span className="font-sans text-sm font-medium leading-[120%] text-[#1F2937]">
                                    Contact support
                                    <span className="block text-xs font-normal">Get help with setup or ask a question.</span>
                                </span>
                            </a>
                            <a href="https://finzcard.com" target="_blank" rel="noreferrer" className="flex items-start gap-2 py-2">
                                <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#1F2937]" strokeWidth={1.75} />
                                <span className="font-sans text-sm font-medium leading-[120%] text-[#1F2937]">
                                    Browse Help Center
                                    <span className="block text-xs font-normal">Guides and answers to get you up & running.</span>
                                </span>
                            </a>
                        </div>
                    </div>
                </aside>
            )}

            {shouldShowSetupGuide && !isPanelOpen && (
                <button
                    type="button"
                    onClick={() => setIsPanelOpen(true)}
                    className="fixed right-[-48px] bottom-[234px] z-[1200] flex h-8 w-[128px] -rotate-90 items-center justify-center gap-1.5 rounded-t bg-[#1F2937] px-2.5 py-1.5 text-sm font-semibold leading-[140%] text-white shadow-[0_2px_6px_rgba(0,0,0,0.18)] hover:bg-[#111827]"
                >
                    <ClipboardList size={12} strokeWidth={1.67} />
                    Set Up Guide
                </button>
            )}
        </>
    );
}
